import type { ReactNode } from 'react';

export type GroupConfirmKind = 'remove' | 'cancel-invite' | 'reinvite' | 'resend-invite';

export type GroupConfirmCopy = {
  submitVariant: 'danger' | 'primary';
  title: string;
  message: ReactNode;
  submitLabel: string;
  cancelLabel: string;
};

const renderEmail = (email: string) => <span className="font-medium text-gray-900">{email}</span>;

export function groupConfirmCopy(kind: GroupConfirmKind, count: number, firstEmail: string): GroupConfirmCopy {
  const isSingle = count === 1;

  switch (kind) {
    case 'remove':
      return {
        submitVariant: 'danger',
        title: isSingle ? 'Remove access?' : `Remove access for ${count} readers?`,
        message: isSingle ? (
          <>
            This will remove {renderEmail(firstEmail)} from this group subscription and free up one seat. They&rsquo;ll
            lose access immediately.
          </>
        ) : (
          <>
            This will remove {count} readers from this group subscription and free up {count} seats. They&rsquo;ll lose
            access immediately.
          </>
        ),
        submitLabel: 'Remove access',
        cancelLabel: 'Cancel',
      };

    case 'cancel-invite':
      return {
        submitVariant: 'danger',
        title: isSingle ? 'Cancel invite?' : `Cancel ${count} invites?`,
        message: isSingle ? (
          <>This will cancel the invitation sent to {renderEmail(firstEmail)}. You can invite them again later.</>
        ) : (
          <>This will cancel the invitations sent to {count} readers. You can invite them again later.</>
        ),
        submitLabel: 'Cancel invite',
        cancelLabel: 'Keep invite',
      };

    case 'reinvite':
      return {
        submitVariant: 'primary',
        title: isSingle ? 'Send invite?' : `Send ${count} invites?`,
        message: isSingle ? (
          <>This will send a new invitation to {renderEmail(firstEmail)}. They&rsquo;ll regain access if they accept.</>
        ) : (
          <>This will send new invitations to {count} readers. They&rsquo;ll regain access if they accept.</>
        ),
        submitLabel: 'Send invite',
        cancelLabel: 'Cancel',
      };

    case 'resend-invite':
      return {
        submitVariant: 'primary',
        title: isSingle ? 'Resend invite?' : `Resend ${count} invites?`,
        message: isSingle ? (
          <>This will resend the invitation to {renderEmail(firstEmail)}.</>
        ) : (
          <>This will resend the invitations to {count} readers.</>
        ),
        submitLabel: 'Resend invite',
        cancelLabel: 'Cancel',
      };
  }
}
